//--- START OF FILE RequestService.jsx ---

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import { loadStripe } from "@stripe/stripe-js";

const { FiHome, FiHeart,FiGift , FiMapPin, FiSettings , FiUser, FiPhone,FiDollarSign, FiMail, FiCalendar, FiMessageSquare, FiCheckCircle, FiArrowLeft } = FiIcons;

const RequestService = () => {
  // --- FIX: Destructure `session` which is now provided by AuthContext ---
  const { user, session } = useAuth(); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false); // For UX feedback
  const [formData, setFormData] = useState({
    serviceType: searchParams.get('service') || '',
    urgency: 'normal',
    startDate: '',
    endDate: '',
    description: '',
    contactInfo: {
      name: '', // Will be populated by useEffect
      email: '',
      phone: '',
      nriLocation: '',
      indianAddress: ''
    },
    specificRequirements: {
  propertyType: '',
  propertyAddress: '',
  serviceNeeded: '',
  notes: '',

  parentName: '',
  parentAge: '',
  parentAddress: '',
  careRequired: '',
  medicalNotes: '',

  travelerName: '',
  destination: '',
  travelDate: '',
  travelServiceRequired: '',

  eventType: '',
  eventDate: '',
  eventLocation: '',
  guestCount: '',
  celebrationRequirements: '',

  financialRequirement: '',
  currentCountry: '',
  investmentGoals: '',
  budgetRange: '',

  customTitle: '',
  customRequirement: ''
}
  });

  useEffect(() => {
    // This effect runs once the user object is available from the context
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          name: user.full_name || user.email || '',
          email: user.email || '',
          phone: user.phone || '', // Assuming phone might be in user object
          // Add any other pre-fillable fields here
        }
      }));
    }
  }, [user]); // Run this effect when the user object changes

  const services = [
  {
    id: 'property-care',
    name: 'Property Care',
    icon: FiHome,
    description: 'Property maintenance and management services',
    color: 'blue'
  },
  {
    id: 'parental-care',
    name: 'Parental Care',
    icon: FiHeart,
    description: 'Care and support for elderly parents',
    color: 'red'
  },
  {
    id: 'travel-services',
    name: 'Travel Services',
    icon: FiMapPin,
    description: 'Travel planning and support services',
    color: 'green'
  },
  {
    id: 'celebration-services',
    name: 'Celebration Services',
    icon: FiGift,
    description: 'Birthday, Anniversary and Family Celebrations',
    color: 'purple'
  },
  {
    id: 'financial-services',
    name: 'Financial Services',
    icon: FiDollarSign,
    description: 'NRI Investment and Financial Advisory',
    color: 'emerald'
  },
  {
    id: 'custom-services',
    name: 'Custom Services',
    icon: FiSettings,
    description: 'Tell us your requirement and we will create a custom solution',
    color: 'orange'
  }
];

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

  const handleSubmit = async () => {
  if (!user || !session?.access_token) {
    alert("Your session could not be verified. Please try logging in again.");
    return;
  }

  setIsSubmitting(true);
  const stripe = await stripePromise;

  try {

    const cleanServiceDetails = Object.fromEntries(
  Object.entries(formData.specificRequirements)
    .filter(([_, value]) => value !== '' && value !== null)
);

const { data: inserted, error } = await supabase
  .from('service_requests')
  .insert([
    {
      user_id: user.id,
      service_type: formData.serviceType,
      urgency: formData.urgency,
      start_date: formData.startDate || null,
      end_date: formData.endDate || null,
      description: formData.description,
      service_details: cleanServiceDetails,
      status: 'pending',
      created_at: new Date().toISOString()
    }
  ])
  .select()
  .single();

    if (error) throw error;

    // 2️⃣ Create Stripe Checkout Session (pass the created record ID)
    const cleanedData = {
      serviceRequestId: inserted.id,  // link Stripe session with this request
      userId: user.id,
      serviceType: formData.serviceType,
      urgency: formData.urgency,
      startDate: formData.startDate,
      endDate: formData.endDate,
      description: formData.description,
      contactInfo: formData.contactInfo,
      specificRequirements: formData.specificRequirements,
      clientUrl: window.location.origin
    };

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_FUNCTION_URL}/create-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(cleanedData),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create Stripe checkout session');
    }

    const data = await response.json();

    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error("A redirect URL was not received from the server.");
    }
  } catch (err) {
    console.error("Error submitting request:", err);
    alert(`Error: ${err.message}`);
    setIsSubmitting(false);
  }
};


  const getServiceColor = (serviceId) => {
    return services.find(s => s.id === serviceId)?.color || 'blue';
  };

  // --- OMITTING renderStep1, renderStep2, renderStep3, renderStep4 for brevity ---
  // --- (They remain exactly the same as in your original file) ---
  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Select Service Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => handleInputChange(null, 'serviceType', service.id)}
              className={`p-6 rounded-xl border-2 transition-all ${
                formData.serviceType === service.id
                  ? `border-${service.color}-500 bg-${service.color}-50`
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-12 h-12 bg-${service.color}-100 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                <SafeIcon icon={service.icon} className={`text-2xl text-${service.color}-600`} />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{service.name}</h4>
              <p className="text-sm text-gray-600">{service.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Urgency Level
        </label>
        <select
          value={formData.urgency}
          onChange={(e) => handleInputChange(null, 'urgency', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="low">Low - Can wait 1-2 weeks</option>
          <option value="normal">Normal - Within a week</option>
          <option value="high">High - Within 2-3 days</option>
          <option value="urgent">Urgent - Immediate attention needed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Start Date
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange(null, 'startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service End Date (Optional)
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange(null, 'endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange(null, 'description', e.target.value)}
          rows={4}
          placeholder="Please describe your specific requirements and any additional details..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <input
            type="text"
            value={formData.contactInfo.name}
            onChange={(e) => handleInputChange('contactInfo', 'name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={formData.contactInfo.email}
            onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            value={formData.contactInfo.phone}
            onChange={(e) => handleInputChange('contactInfo', 'phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Location (NRI)
          </label>
          <input
            type="text"
            value={formData.contactInfo.nriLocation}
            onChange={(e) => handleInputChange('contactInfo', 'nriLocation', e.target.value)}
            placeholder="e.g., New York, USA"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address in India
        </label>
        <textarea
          value={formData.contactInfo.indianAddress}
          onChange={(e) => handleInputChange('contactInfo', 'indianAddress', e.target.value)}
          rows={3}
          placeholder="Complete address where service is needed..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </motion.div>
  );

  const renderStep3 = () => (

    
    <motion.div
  initial={{ opacity: 0, x: 20 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.4 }}
  className="space-y-6"
>
  <h3 className="text-xl font-bold text-gray-900 mb-4">
    Service-Specific Requirements
  </h3>

  {/* Prefilled from Step 1 Service Description */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Requirement Details
    </label>

    <textarea
      rows={5}
      value={formData.description}
      onChange={(e) =>
        handleInputChange(null, 'description', e.target.value)
      }
      placeholder="Describe your service requirements..."
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
    />
  </div>
      
      {formData.serviceType === 'property-care' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Type
            </label>
            <select
              value={formData.specificRequirements.propertyType}
              onChange={(e) => handleInputChange('specificRequirements', 'propertyType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select property type</option>
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="house">Independent House</option>
              <option value="commercial">Commercial Property</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Address
            </label>
            <textarea
              value={formData.specificRequirements.propertyAddress}
              onChange={(e) => handleInputChange('specificRequirements', 'propertyAddress', e.target.value)}
              rows={3}
              placeholder="Complete property address..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {formData.serviceType === 'parental-care' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent's Age
              </label>
              <input
                type="number"
                value={formData.specificRequirements.parentAge}
                onChange={(e) => handleInputChange('specificRequirements', 'parentAge', e.target.value)}
                placeholder="Age"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent's Location
              </label>
              <input
                type="text"
                value={formData.specificRequirements.parentLocation}
                onChange={(e) => handleInputChange('specificRequirements', 'parentLocation', e.target.value)}
                placeholder="City, State"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Health Conditions (if any)
            </label>
            <textarea
              value={formData.specificRequirements.healthConditions}
              onChange={(e) => handleInputChange('specificRequirements', 'healthConditions', e.target.value)}
              rows={3}
              placeholder="Please mention any health conditions, medications, or special care requirements..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {formData.serviceType === 'travel-services' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Travel Dates
              </label>
              <input
                type="text"
                value={formData.specificRequirements.travelDates}
                onChange={(e) => handleInputChange('specificRequirements', 'travelDates', e.target.value)}
                placeholder="e.g., Dec 15 - Jan 15, 2024"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Size
              </label>
              <input
                type="number"
                value={formData.specificRequirements.groupSize}
                onChange={(e) => handleInputChange('specificRequirements', 'groupSize', e.target.value)}
                placeholder="Number of travelers"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Needs/Preferences
            </label>
            <textarea
              value={formData.specificRequirements.specialNeeds}
              onChange={(e) => handleInputChange('specificRequirements', 'specialNeeds', e.target.value)}
              rows={3}
              placeholder="Any special requirements, preferences, or accessibility needs..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {formData.serviceType === 'celebration-services' && (
  <div className="space-y-4">

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Event Type
      </label>

      <select
        value={formData.specificRequirements.eventType}
        onChange={(e) =>
          handleInputChange(
            'specificRequirements',
            'eventType',
            e.target.value
          )
        }
        className="w-full px-3 py-2 border rounded-lg"
      >
        <option value="">Select Event</option>
        <option>Birthday</option>
        <option>Anniversary</option>
        <option>House Warming</option>
        <option>Baby Shower</option>
        <option>Other</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Event Date
      </label>

      <input
        type="date"
        value={formData.specificRequirements.eventDate}
        onChange={(e) =>
          handleInputChange(
            'specificRequirements',
            'eventDate',
            e.target.value
          )
        }
        className="w-full px-3 py-2 border rounded-lg"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Location
      </label>

      <input
        type="text"
        value={formData.specificRequirements.eventLocation}
        onChange={(e) =>
          handleInputChange(
            'specificRequirements',
            'eventLocation',
            e.target.value
          )
        }
        className="w-full px-3 py-2 border rounded-lg"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Number of Guests
      </label>

      <input
        type="number"
        value={formData.specificRequirements.guestCount}
        onChange={(e) =>
          handleInputChange(
            'specificRequirements',
            'guestCount',
            e.target.value
          )
        }
        className="w-full px-3 py-2 border rounded-lg"
      />
    </div>

    <textarea
      rows={4}
      placeholder="Celebration Requirements"
      value={formData.specificRequirements.celebrationRequirements}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'celebrationRequirements',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
)}

      {formData.serviceType === 'financial-services' && (
  <div className="space-y-4">

    <input
      type="text"
      placeholder="Financial Requirement"
      value={formData.specificRequirements.financialRequirement}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'financialRequirement',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />

    <input
      type="text"
      placeholder="Current Country"
      value={formData.specificRequirements.currentCountry}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'currentCountry',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />

    <textarea
      rows={4}
      placeholder="Investment Goals"
      value={formData.specificRequirements.investmentGoals}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'investmentGoals',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />

    <input
      type="text"
      placeholder="Budget Range"
      value={formData.specificRequirements.budgetRange}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'budgetRange',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
)}
      {formData.serviceType === 'custom-services' && (
  <div className="space-y-4">

    <input
      type="text"
      placeholder="Service Title"
      value={formData.specificRequirements.customTitle}
      onChange={(e) =>
        handleInputChange(
          'specificRequirements',
          'customTitle',
          e.target.value
        )
      }
      className="w-full px-3 py-2 border rounded-lg"
    />

    
  </div>
)}
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-bold text-gray-900 mb-4">Review Your Request</h3>
      
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Service Details</h4>
          <p className="text-gray-700">Service: {services.find(s => s.id === formData.serviceType)?.name}</p>
          <p className="text-gray-700">Urgency: {formData.urgency}</p>
          <p className="text-gray-700">Start Date: {formData.startDate || 'Not specified'}</p>
          {formData.endDate && <p className="text-gray-700">End Date: {formData.endDate}</p>}
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
          <p className="text-gray-700">Name: {formData.contactInfo.name}</p>
          <p className="text-gray-700">Email: {formData.contactInfo.email}</p>
          <p className="text-gray-700">Phone: {formData.contactInfo.phone}</p>
          <p className="text-gray-700">NRI Location: {formData.contactInfo.nriLocation}</p>
        </div>

        {formData.description && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
            <p className="text-gray-700">{formData.description}</p>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-lg mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">What happens next?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Our team will review your request within 2-4 hours</li>
              <li>• You'll receive a detailed service plan and quote</li>
              <li>• A dedicated service coordinator will be assigned</li>
              <li>• Service will begin as per your preferred timeline</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );


  // --- FIX: The loading and user check is no longer needed here. ---
  // The AuthProvider wrapper (`!loading && children`) handles this.
  // If this component renders, we are guaranteed to have a user.

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <SafeIcon icon={FiArrowLeft} className="text-lg" />
            <span>Back</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Service</h1>
          <p className="text-gray-600">Tell us about your service requirements and we'll take care of the rest</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
           {/* ... no changes to progress steps JSX ... */}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1 || isSubmitting}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {currentStep < 4 ? (
              <button
                onClick={() => setCurrentStep(Math.min(4, currentStep + 1))}
                disabled={
                  (currentStep === 1 && !formData.serviceType) ||
                  (currentStep === 2 && (!formData.contactInfo.name || !formData.contactInfo.email))
                }
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-wait"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestService;